import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent,
} from 'firebase/analytics';
import { getFirebaseApp } from './firebase';

let analyticsPromise: Promise<Analytics | null> | null = null;

const getAnalyticsInstance = (): Promise<Analytics | null> => {
  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      if (typeof window === 'undefined') return null;
      try {
        if (!(await isSupported())) return null;
        return getAnalytics(getFirebaseApp());
      } catch {
        return null;
      }
    })();
  }
  return analyticsPromise;
};

const safeLog = (name: string, params?: Record<string, unknown>) => {
  void getAnalyticsInstance().then((analytics) => {
    if (!analytics) return;
    try {
      logEvent(analytics, name, params as Record<string, string | number | boolean>);
    } catch {
      // never block UI on analytics
    }
  });
};

/** Paystack live keys + NODE_ENV production — payment amounts only in prod. */
export const isProdPayments = () => process.env.NODE_ENV === 'production';

export const logPageVisit = (path: string) => {
  safeLog('page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : path,
    page_title: typeof document !== 'undefined' ? document.title : path,
  });
};

export const logButtonClick = (
  label: string,
  extra?: Record<string, string | number | boolean | undefined>,
) => {
  const cleaned = (label || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  if (!cleaned) return;
  const params: Record<string, string | number | boolean> = { button_label: cleaned };
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined) params[k] = v;
    });
  }
  safeLog('button_click', params);
};

/**
 * Successful Paystack / subscription charge.
 * Amount must be in major currency units (e.g. NGN, not kobo).
 * No-ops outside production.
 */
export const logPaymentSuccess = (params: {
  amount: number;
  currency?: string;
  planId?: string;
  planTitle?: string;
  reference?: string;
  kind?: string;
}) => {
  if (!isProdPayments()) return;
  const value = Number(params.amount);
  if (!Number.isFinite(value) || value < 0) return;

  const purchase: Record<string, string | number> = {
    value,
    currency: params.currency || 'NGN',
  };
  if (params.reference) purchase.transaction_id = params.reference;
  if (params.planId) purchase.item_id = params.planId;
  if (params.planTitle) purchase.item_name = params.planTitle;
  if (params.kind) purchase.payment_kind = params.kind;
  safeLog('purchase', purchase);

  const custom: Record<string, string | number> = {
    value,
    currency: params.currency || 'NGN',
  };
  if (params.planId) custom.plan_id = params.planId;
  if (params.planTitle) custom.plan_title = params.planTitle;
  if (params.reference) custom.reference = params.reference;
  if (params.kind) custom.kind = params.kind;
  safeLog('payment_success', custom);
};

const clickTargetSelector = [
  'button',
  'a[href]',
  '[role="button"]',
  'input[type="submit"]',
  'input[type="button"]',
].join(',');

const labelForClickTarget = (el: HTMLElement): string => {
  const explicit = el.getAttribute('data-analytics')
    || el.getAttribute('aria-label')
    || el.getAttribute('title')
    || (el as HTMLInputElement).value;
  if (explicit && String(explicit).trim()) return String(explicit).trim();
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
};

/** Route page_view + auto button_click for controls that are not manually tagged. */
export const AnalyticsTracker = function AnalyticsTracker() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    logPageVisit(`${pathname}${search || ''}`);
  }, [pathname, search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const raw = event.target;
      if (!(raw instanceof Element)) return;
      const el = raw.closest(clickTargetSelector) as HTMLElement | null;
      if (!el) return;
      // Already logged elsewhere, or explicitly skipped.
      if (el.hasAttribute('data-analytics-logged') || el.hasAttribute('data-analytics-skip')) {
        return;
      }
      const label = labelForClickTarget(el);
      if (!label) return;
      logButtonClick(label, {
        tag: el.tagName.toLowerCase(),
        path: window.location.pathname,
      });
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return null;
};
